import PropTypes from 'prop-types';
import DateTimePicker from '@mui/lab/DateTimePicker';
import config from '../../../app/config';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import DatePicker from '@mui/lab/DatePicker';
import TimePicker from '@mui/lab/TimePicker';
import {MobileDatePicker, MobileDateTimePicker, MobileTimePicker} from '@mui/lab';
import clsx from 'clsx';
import {AgyleCalendarIcon} from '../../icons/agyle';

const pickerStyle = makeStyles(theme => ({
    textField: {
        width: '100%',
        backgroundColor: theme.palette.background.field,
        borderRadius: ({noBorder}) => !noBorder ? "unset" : 10,
        "& fieldset": {
            border: ({noBorder}) => noBorder ? "none" : '',
            borderColor: ({error, noBorder}) => !noBorder && error ? theme.palette.error.main : '',
        },
    },
    font: {
        fontFamily: 'Inter',
        fontWeight: 500,
    },
    background: {
        backgroundColor: theme.palette.background.field,
    },
    inPatientBackground: {
        backgroundColor: `${theme.palette.inPatient.inputBackground} !important`,
    },
}));

const AgyleDatePicker = (props) => {
    const classes = pickerStyle({noBorder: props.noBorder, error: props.error});

    switch (props.type) {
        case 'date':
            return (
                <DatePicker
                    views={props.views}
                    onChange={props.onChange}
                    value={props.value}
                    renderInput={(inp) => <TextField
                        size={props.size}
                        required={props.required}
                        className={clsx(
                            classes.textField,
                            props.withNewBackground && classes.inPatientBackground,
                            props.textFieldClass,
                        )}
                        color="tertiary"
                        sx={{label: props.error && {color: 'error.main'}}}
                        {...inp}
                    />}
                    inputFormat={config.defaultDateFormat}
                    label={props.label}
                    autoOk={props.autoOk}
                    style={props.componentStyle}
                    disableToolbar={props.disableToolbar}
                    disableFuture={props.disableFuture}
                    disablePast={props.disablePast}
                    disabled={props.disabled}
                    maxDate={props.maxDate}
                    minDate={props.minDate}
                    onError={props.onError}
                    readOnly={props.readOnly}
                    components={{
                        OpenPickerIcon: AgyleCalendarIcon,
                    }}
                    className={props.className}
                    PopperProps={{
                        className: 'scroll-normal-white'
                    }}
                    PaperProps={{
                        className: 'scroll-normal-white'
                    }}
                    InputProps={{
                        classes: {
                            input: props.classInput,
                            root: props.classRoot,
                        },
                    }}
                />
            );

        case 'time':
            return (
                <TimePicker
                    onChange={props.onChange}
                    value={props.value}
                    renderInput={(inp) => <TextField
                        size={props.size}
                        required={props.required}
                        className={clsx(
                            classes.textField,
                            props.withNewBackground && classes.inPatientBackground,
                            props.textFieldClass,
                        )}
                        color="tertiary"
                        sx={{
                            label: props.error && {color: 'error.main'},
                            svg: {color: props.svgColor},
                        }}
                        {...inp}
                    />}
                    inputFormat={config.defaultTimeFormat}
                    label={props.label}
                    autoOk={props.autoOk}
                    disableToolbar={props.disableToolbar}
                    disabled={props.disabled}
                    maxTime={props.maxDate ?? props.maxTime}
                    minTime={props.minDate ?? props.minTime}
                    ampm={false}
                    onError={props.onError}
                    readOnly={props.readOnly}
                    className={props.className}
                    InputProps={{
                        classes: {
                            input: props.classInput,
                        },
                    }}
                />
            );

        case 'mobileDate':
            return (
                <MobileDatePicker
                    views={props.views}
                    onChange={props.onChange}
                    value={props.value}
                    renderInput={(inp) => <TextField
                        required={props.required}
                        size={props.size}
                        className={clsx(
                            classes.textField,
                            props.withNewBackground && classes.inPatientBackground,
                            props.textFieldClass,
                        )}
                        color="tertiary"
                        sx={{label: props.error && {color: 'error.main'}}}
                        {...inp}
                    />}
                    inputFormat={config.defaultDateFormat}
                    label={props.label}
                    autoOk={props.autoOk}
                    disableToolbar={props.disableToolbar}
                    disableFuture={props.disableFuture}
                    disablePast={props.disablePast}
                    disabled={props.disabled}
                    maxDate={props.maxDate}
                    minDate={props.minDate}
                    onError={props.onError}
                    readOnly={props.readOnly}
                    components={{
                        OpenPickerIcon: AgyleCalendarIcon,
                    }}
                    className={props.className}
                    InputProps={{
                        classes: {
                            input: props.classInput,
                        },
                    }}
                />
            );

        case 'mobileDateTime':
            return (
                <MobileDateTimePicker
                    views={props.views}
                    onChange={props.onChange}
                    value={props.value}
                    renderInput={(inp) => <TextField
                        required={props.required}
                        size={props.size}
                        className={clsx(
                            classes.textField,
                            props.withNewBackground && classes.inPatientBackground,
                            props.textFieldClass,
                        )}
                        color="tertiary"
                        sx={{label: props.error && {color: 'error.main'}}}
                        {...inp}
                    />}
                    inputFormat={config.defaultDateFormat}
                    label={props.label}
                    autoOk={props.autoOk}
                    disableToolbar={props.disableToolbar}
                    disableFuture={props.disableFuture}
                    disablePast={props.disablePast}
                    disabled={props.disabled}
                    maxDateTime={props.maxDate ?? props.maxDateTime}
                    minDateTime={props.minDate ?? props.minDateTime}
                    onError={props.onError}
                    readOnly={props.readOnly}
                    components={{
                        OpenPickerIcon: AgyleCalendarIcon,
                    }}
                    className={props.className}
                    InputProps={{
                        classes: {
                            input: props.classInput,
                        },
                    }}
                />
            );

        case 'mobileTime':
            return (
                <MobileTimePicker
                    views={props.views}
                    onChange={props.onChange}
                    value={props.value}
                    renderInput={(inp) => <TextField
                        size={props.size}
                        required={props.required}
                        className={clsx(
                            classes.textField,
                            props.withNewBackground && classes.inPatientBackground,
                            props.textFieldClass,
                        )}
                        color="tertiary"
                        sx={{label: props.error && {color: 'error.main'}}}
                        {...inp}
                    />}
                    inputFormat={config.defaultDateFormat}
                    label={props.label}
                    autoOk={props.autoOk}
                    disableToolbar={props.disableToolbar}
                    disableFuture={props.disableFuture}
                    disablePast={props.disablePast}
                    disabled={props.disabled}
                    maxTime={props.maxDate ?? props.maxTime}
                    minTime={props.minDate ?? props.minTime}
                    onError={props.onError}
                    readOnly={props.readOnly}
                    className={props.className}
                    InputProps={{
                        classes: {
                            input: props.classInput,
                        },
                    }}
                />
            );

        case 'dateTime':
        default:
            return (
                <DateTimePicker
                    onChange={props.onChange}
                    value={props.value}
                    renderInput={(inp) => <TextField
                        size={props.size}
                        required={props.required}
                        className={clsx(
                            classes.textField,
                            props.withNewBackground && classes.inPatientBackground,
                            props.textFieldClass,
                        )}
                        color="tertiary"
                        sx={{label: props.error && {color: 'error.main'}}}
                        {...inp}
                    />}
                    inputFormat={config.defaultDateTimeFormat}
                    label={props.label}
                    autoOk={props.autoOk}
                    disableToolbar={props.disableToolbar}
                    disableFuture={props.disableFuture}
                    disablePast={props.disablePast}
                    disabled={props.disabled}
                    maxDateTime={props.maxDate ?? props.maxDateTime}
                    minDateTime={props.minDate ?? props.minDateTime}
                    ampm={false}
                    onError={props.onError}
                    readOnly={props.readOnly}
                    components={{
                        OpenPickerIcon: AgyleCalendarIcon,
                    }}
                    className={props.className}
                    InputProps={{
                        classes: {
                            input: props.classInput,
                            root: props.classRoot,
                        },
                    }}
                />
            );
    }
};

export default AgyleDatePicker;

AgyleDatePicker.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func,
    onError: PropTypes.func,
    disabled: PropTypes.bool,
    autoOk: PropTypes.bool,
    disableToolbar: PropTypes.bool,
    disableFuture: PropTypes.bool,
    disablePast: PropTypes.bool,
    readOnly: PropTypes.bool,
    error: PropTypes.bool,
    noBorder: PropTypes.bool,
    withNewBackground: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    type: PropTypes.oneOf(['date', 'time', 'dateTime', 'mobileDate', 'mobileDateTime', 'mobileTime']),
    maxDate: PropTypes.any,
    minDate: PropTypes.any,
    textFieldClass: PropTypes.any,
    className: PropTypes.any,
    classInput: PropTypes.any,
    required: PropTypes.bool,
    svgColor: PropTypes.string,
};

AgyleDatePicker.defaultProps = {
    value: null,
    maxDate: null,
    minDate: null,
    autoOk: true,
    disableToolbar: true,
    disableFuture: true,
    readOnly: false,
    disablePast: false,
    noBorder: true,
    withNewBackground: false,
    size: 'small',
    type: 'dateTime',
    label: '',
    required: false,
};